/**
 * Official Indian Law Enforcement Email Validation
 *
 * Allowed domains / patterns:
 *  - @mha.gov.in          → Ministry of Home Affairs
 *  - @ips.gov.in          → IPS / central government officials
 *  - @police.gov.in       → generic national police
 *  - @nic.in              → National Informatics Centre (govt mail)
 *  - @gov.in              → any official government domain
 *  - @<state>police.gov.in → e.g. assampolice.gov.in, uppolice.gov.in
 */

// ── DEV TESTING WHITELIST ──────────────────────────────────────────────────
// Emails listed here bypass domain restrictions for local testing.

const DEV_WHITELIST = [
  "pranav.cs22@sahyadri.edu.in",
  "ananthprabhu@sahyadri.edu.in",
  "torsecure.dev@gmail.com",
  "sathwikkamath31@gmail.com",
  "shravanu.k887@gmail.com",
  "sarvasva5758@gmail.com",
];

// Exact allowed domains (checked literally after the @)
const ALLOWED_EXACT_DOMAINS = [
  "mha.gov.in",
  "ips.gov.in",
  "police.gov.in",
  "nic.in",
  "gov.in",
];

// Suffix patterns: any domain that ends with these strings is valid
const ALLOWED_SUFFIX_PATTERNS = [
  ".gov.in",   // covers statepolice.gov.in, mha.gov.in, ips.gov.in, etc.
  ".nic.in",   // covers state NIC subdomains  e.g. up.nic.in
];

/**
 * Returns true if the given email address belongs to an
 * official Indian law enforcement / government domain.
 *
 * @param {string} email
 * @returns {boolean}
 */
export function isOfficialGovEmail(email) {
  if (!email || typeof email !== "string") return false;

  const lower = email.toLowerCase().trim();

  // 0. Dev whitelist — bypass all domain checks
  if (DEV_WHITELIST.includes(lower)) return true;

  const atIdx = lower.lastIndexOf("@");
  if (atIdx === -1) return false;

  const domain = lower.slice(atIdx + 1); // everything after @

  // 1. Exact match
  if (ALLOWED_EXACT_DOMAINS.includes(domain)) return true;

  // 2. Suffix match  (e.g. assampolice.gov.in ends with .gov.in)
  for (const suffix of ALLOWED_SUFFIX_PATTERNS) {
    if (domain.endsWith(suffix)) return true;
  }

  return false;
}

/**
 * Returns a human-readable error message if the email is not allowed,
 * or null if the email is valid.
 *
 * @param {string} email
 * @returns {string|null}
 */
export function getEmailDomainError(email) {
  if (!email) return null; // let 'required' validators handle empty
  if (!isOfficialGovEmail(email)) {
    return (
      "⛔ Only official Indian law enforcement email addresses are allowed. " +
      "Valid domains include @gov.in, @nic.in, @mha.gov.in, @ips.gov.in, " +
      "@police.gov.in, or state police domains (e.g. @assampolice.gov.in)."
    );
  }
  return null;
}
